// A user's profile copy can drift from the Cognito/IdP mirror on either their
// sign-in email or their role. This is the single source of truth for that
// comparison so the list badge, the drawer, and the filter all agree — and it
// mirrors the backend's syncMatch=diff predicate (email OR role differs, each
// counted only when both sides are present).
interface CognitoComparable {
  email?: string
  cognitoEmail?: string
  cognitoRole?: string
  cognitoUserRole?: string
  hasNoCognitoRecord?: boolean
}

// Sign-in (Cognito) email differs from the profile email specifically. The
// drawer's Sign-in row gates on this (not the combined mismatch) so it isn't
// shown — rendering the same address twice — when only the role differs.
export const cognitoEmailDiffers = (u: CognitoComparable): boolean =>
  !!u.cognitoEmail && !!u.email && u.cognitoEmail !== u.email

// Cognito role (from the mirror) differs from the profile role. Exported so the
// drawer's role row gates on this alone, matching the Sign-in email row pattern.
export const cognitoRoleDiffers = (u: CognitoComparable): boolean =>
  !!u.cognitoUserRole && !!u.cognitoRole && u.cognitoUserRole !== u.cognitoRole

export interface CognitoWarning {
  key: 'email' | 'role' | 'missing'
  label: string
  // Absent on the 'missing' warning: there is no mirror row, so there is no
  // second value to compare against — only the explanation carries meaning.
  profileValue?: string
  cognitoValue?: string
  detail?: string
}

// One entry per problem found, so the badge popover can list them individually
// instead of collapsing them into a single sentence. A missing mirror row is
// reported alone: with no Cognito values to compare, no field can be shown to
// differ, so the per-field warnings below would all be silently false.
export const cognitoWarnings = (u: CognitoComparable): CognitoWarning[] => {
  if (u.hasNoCognitoRecord) {
    return [
      {
        key: 'missing',
        label: 'No Cognito record',
        detail:
          'No active Cognito user is linked to this profile, so they likely cannot sign in.',
      },
    ]
  }
  const warnings: CognitoWarning[] = []
  if (cognitoEmailDiffers(u)) {
    warnings.push({
      key: 'email',
      label: 'Email mismatch',
      profileValue: u.email,
      cognitoValue: u.cognitoEmail,
    })
  }
  if (cognitoRoleDiffers(u)) {
    warnings.push({
      key: 'role',
      label: 'Role mismatch',
      profileValue: u.cognitoRole,
      cognitoValue: u.cognitoUserRole,
    })
  }
  return warnings
}
